import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'pt' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.gallery': 'Gallery',
    'nav.contact': 'Contact',

    // Home Page
    'home.hero.title': 'Magical Kids Sleepover Parties',
    'home.hero.subtitle': 'Create unforgettable memories with our themed tent parties.\nPerfect for ages 6-15',
    'home.hero.cta': 'Book Your Party Today',
    'home.services.title': 'Why Choose Cabanna\'s Party?',
    'home.service1.title': 'Complete Setup Service',
    'home.service1.desc': 'All decoration set up and picked up the next day at desired time! We are committed to provide the best experience for your party!',
    'home.service2.title': 'Custom Decorations',
    'home.service2.desc': 'Decoration theme can be selected by the customer, and includes: Customized stand for cake and candies, Decorative Picture signs, Balloon Cloud, Artificial bonfire and much more!',
    'home.service3.title': 'Amazing Extras',
    'home.service3.desc': 'Extra material is included: Goodie bags, Karaoke microphones, games, and so much more to make your party unforgettable!',
    'home.feature1.title': 'Fun Guaranteed!',
    'home.feature1.desc': 'Our themed sleepover parties are perfect for slumber parties and create magical experiences for the whole family.',
    'home.feature2.title': 'Free Delivery',
    'home.feature2.desc': 'We provide free delivery and setup of our themed sleepover tents, making your party planning stress-free.',
    'home.feature3.title': 'Orlando Based',
    'home.feature3.desc': 'We are based in Orlando and committed to providing the best party experience for kids throughout Central Florida.',
    'home.testimonials.title': 'What Our Celebrity Clients Say',
    'home.testimonial1.text': 'Cabanna\'s Party made my daughter\'s birthday absolutely magical! The attention to detail was incredible.',
    'home.testimonial1.author': 'Deborah Secco',
    'home.testimonial1.role': 'Actress & TV Host',
    'home.testimonial2.text': 'The best party experience ever! My kids still talk about their amazing sleepover party.',
    'home.testimonial2.author': 'Fernanda Pontes',
    'home.testimonial2.role': 'Actress',
    'home.testimonial3.text': 'Professional, creative, and so much fun! Highly recommend for any special celebration.',
    'home.testimonial3.author': 'Deive Leonardo',
    'home.testimonial3.role': 'Actor & Singer',
    'home.testimonial4.text': 'Perfect party setup! The kids had the time of their lives. Thank you Cabanna\'s!',
    'home.testimonial4.author': 'Lori Improta',
    'home.testimonial4.role': 'Fitness Influencer',
    'home.testimonial5.text': 'Amazing service and beautiful decorations. Made our family celebration unforgettable!',
    'home.testimonial5.author': 'Nivea Stelmann',
    'home.testimonial5.role': 'Actress & Model',
    'home.themes.title': 'Popular Party Themes',
    'home.themes.description': 'Discover our most popular kids party themes in Orlando. From princess parties to superhero adventures, we create magical sleepover experiences with themed tents, decorations, and complete setup service.',
    'home.theme.princess': 'Princess Party',
    'home.theme.unicorn': 'Unicorn Magic',
    'home.theme.pokemon': 'Pokemon Adventure',
    'home.theme.superhero': 'Superhero Squad',
    'home.cta.title': 'Sleep Over Party Means Lots of Fun!',
    'home.cta.subtitle': 'Book now without commitments and let us create the perfect party experience for your child.',
    'home.cta.button': 'Call Our Party Director',
    'home.themes.viewall': 'View All Themes',

    // Gallery Page
    'gallery.title': 'Party Gallery',
    'gallery.subtitle': 'Browse through our amazing party setups and themes.\nEach party is uniquely designed to create magical memories!',
    'gallery.instagram.title': '🌟 Latest Party Photos',
    'gallery.instagram.desc': 'Follow us on Instagram to see all our latest party setups and get inspiration for your child\'s special day!',
    'gallery.instagram.button': 'Follow us on Instagram',
    'gallery.themes.title': 'Party Themes',
    'gallery.tent.types.title': 'Choose Your Perfect Setup',
    'gallery.tent.types.subtitle': 'We offer three amazing setup options to fit your space and party style perfectly',
    'gallery.tent.small.title': 'TeePee',
    'gallery.tent.small.desc': 'Cozy and intimate indoor setup perfect for smaller spaces. Features individual sleeping areas with magical decorations and fairy lights for an enchanting sleepover experience.',
    'gallery.tent.canopy.title': 'Indoor Canopy',
    'gallery.tent.canopy.desc': 'Open-air indoor party setup without tent walls. Perfect for larger indoor spaces, offering flexibility and room to move while maintaining the magical sleepover atmosphere.',
    'gallery.tent.big.title': 'Big Cabana',
    'gallery.tent.big.desc': 'Our premium outdoor glamping experience! Perfect for backyards, patios, or outdoor spaces. Spacious luxury setup with weather-resistant materials and extra amenities for the ultimate outdoor sleepover adventure.',
    'gallery.process.title': 'Our Magical Setup Process',
    'gallery.process.subtitle': 'From consultation to cleanup, we handle everything for your perfect party',
    'gallery.process.step1.title': 'Consultation',
    'gallery.process.step1.desc': 'We discuss your theme preferences, party details, and special requests',
    'gallery.process.step2.title': 'Professional Setup',
    'gallery.process.step2.desc': 'Our expert team arrives and creates the perfect magical party environment',
    'gallery.process.step3.title': 'Party Time!',
    'gallery.process.step3.desc': 'Kids enjoy their unforgettable magical sleepover experience',
    'gallery.process.step4.title': 'Easy Cleanup',
    'gallery.process.step4.desc': 'We return the next day to clean up everything - no stress for parents!',
    'gallery.cta.title': 'Ready to Create Magical Memories?',
    'gallery.cta.subtitle': 'Contact us today to book your child\'s dream party!',
    'gallery.cta.button': 'Call Our Party Director',

    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Ready to plan the perfect party?\nGet in touch with our party planning experts!',
    'contact.phone.title': 'Call Our Party Director',
    'contact.phone.hours': 'Available 7 days a week\n9 AM - 8 PM',
    'contact.email.title': 'Email Us',
    'contact.email.response': 'We respond within 24 hours',
    'contact.location.title': 'Service Area',
    'contact.location.area': 'Orlando & Central Florida',
    'contact.location.delivery': 'Free delivery within 25 miles',
    'contact.booking.title': 'Booking Notice',
    'contact.booking.notice': '2 weeks advance preferred',
    'contact.booking.rush': 'Rush orders available',
    'contact.form.title': 'Request a Quote',
    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.q1': 'How far in advance should I book?',
    'contact.faq.a1': 'We recommend booking at least 2 weeks in advance, especially for weekends. However, we can accommodate rush orders when possible.',
    'contact.faq.q2': 'What\'s included in the rental?',
    'contact.faq.a2': 'Each tent includes bedding, pillows, themed decorations, fairy lights, and a personalized name sign. We also provide setup and pickup service.',
    'contact.faq.q3': 'Do you provide food or entertainment?',
    'contact.faq.a3': 'We focus on the sleepover setup and decorations. We can recommend trusted local vendors for catering, entertainment, and party supplies.',
    'contact.faq.q4': 'What if it rains?',
    'contact.faq.a4': 'Our tents are designed for indoor use. We\'ll work with you to ensure your party space is ready, rain or shine!',
    'contact.faq.q5': 'How do I upload photos to your gallery?',
    'contact.faq.a5': 'Follow us on Instagram! When you tag us in your party photos or send them to us, we\'ll feature them on our Instagram feed and website gallery.',
    'contact.faq.q6': 'Do you travel outside Orlando?',
    'contact.faq.a6': 'Yes! We provide free delivery within 25 miles of Orlando. For locations beyond that, a small travel fee may apply.',
    'contact.whatsapp.title': 'WhatsApp Us',
    'contact.whatsapp.subtitle': 'Quick response guaranteed',
    'contact.whatsapp.message': 'Hi! I\'m interested in booking a magical sleepover party. Can you help me with more information?',
    'contact.service.title': 'Service Information',
    'contact.service.subtitle': 'Everything you need to know about our service area and booking requirements',

    // Footer
    'footer.description': 'Creating magical sleepover experiences for kids throughout Central Florida.',
    'footer.quicklinks': 'Quick Links',
    'footer.services': 'Our Services',
    'footer.contact': 'Contact Info',
    'footer.location': 'Orlando, FL & Surrounding Areas',
    'footer.copyright': '© 2026 Cabanna\'s Party Rentals. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',

    // Gallery Themes Data
    'gallery.themes.search.placeholder': 'Search party themes...',
    'gallery.themes.custom.note': 'Don\'t see your theme? Contact our party manager to create a custom theme just for you!',
    'gallery.themes.no.results': 'No themes found matching "{query}". Contact our party manager for custom themes!',
    'gallery.themes.princess.title': 'Princess Party',
    'gallery.themes.princess.desc': 'Royal princess parties with elegant decorations, tiaras, and magical castle themes for unforgettable celebrations.',
    'gallery.themes.unicorn.title': 'Unicorn Magic',
    'gallery.themes.unicorn.desc': 'Colorful rainbow unicorn parties with sparkly decorations and magical accessories for dreamy sleepovers.',
    'gallery.themes.pokemon.title': 'Pokemon Adventure',
    'gallery.themes.pokemon.desc': 'Action-packed Pokemon adventure parties with character decorations and exciting themed elements.',
    'gallery.themes.superhero.title': 'Superhero Squad',
    'gallery.themes.superhero.desc': 'Hero-themed superhero parties with capes, masks, and action-packed decorations for brave adventurers.',
    'gallery.themes.space.title': 'Space Adventure',
    'gallery.themes.space.desc': 'Galactic space themes with stars, planets, astronauts, and cosmic decorations for out-of-this-world fun.',
    'gallery.themes.mermaid.title': 'Mermaid Dreams',
    'gallery.themes.mermaid.desc': 'Under-the-sea mermaid parties with ocean blues, shells, and magical underwater decorations.',
    'gallery.themes.dinosaur.title': 'Dinosaur Discovery',
    'gallery.themes.dinosaur.desc': 'Prehistoric dinosaur adventures with jungle themes and exciting archaeological elements.',
    'gallery.themes.fairy.title': 'Fairy Garden',
    'gallery.themes.fairy.desc': 'Enchanted fairy garden parties with flowers, butterflies, and magical woodland creatures.',
    'gallery.themes.pirate.title': 'Pirate Adventure',
    'gallery.themes.pirate.desc': 'Ahoy matey! Pirate-themed parties with treasure hunts, ships, and swashbuckling adventures.',
    'gallery.themes.minecraft.title': 'Minecraft World',
    'gallery.themes.minecraft.desc': 'Blocky Minecraft adventures with pixelated decorations and creative building themes.',
    'gallery.themes.frozen.title': 'Frozen Winter',
    'gallery.themes.frozen.desc': 'Let it go with Frozen-themed parties featuring ice blues, snowflakes, and winter magic.',
    'gallery.themes.safari.title': 'Safari Adventure',
    'gallery.themes.safari.desc': 'Wild safari expeditions with jungle animals, adventure gear, and exploration themes.',
    'gallery.themes.rainbow.title': 'Rainbow Dreams',
    'gallery.themes.rainbow.desc': 'Colorful rainbow parties with bright decorations, clouds, and magical color themes.',
    'gallery.themes.sports.title': 'Sports Champions',
    'gallery.themes.sports.desc': 'Athletic sports themes with team colors, equipment, and championship celebration decorations.',
    'gallery.themes.art.title': 'Art Studio',
    'gallery.themes.art.desc': 'Creative art parties with painting supplies, colorful decorations, and artistic inspiration.',
    'gallery.themes.music.title': 'Music Festival',
    'gallery.themes.music.desc': 'Musical celebrations with instruments, notes, and concert-style decorations for little rockstars.',
    'gallery.themes.carnival.title': 'Carnival Fun',
    'gallery.themes.carnival.desc': 'Circus carnival themes with bright colors, games, and festive carnival decorations.',
    'gallery.themes.beach.title': 'Beach Party',
    'gallery.themes.beach.desc': 'Tropical beach parties with sand, surf, palm trees, and sunny summer vibes.',
    'gallery.themes.garden.title': 'Garden Party',
    'gallery.themes.garden.desc': 'Beautiful garden themes with flowers, butterflies, and natural outdoor decorations.',
    'gallery.themes.movie.title': 'Movie Night',
    'gallery.themes.movie.desc': 'Hollywood movie themes with red carpets, popcorn, and cinema-style decorations.'
  },
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.gallery': 'Galeria',
    'nav.contact': 'Contato',

    // Home Page
    'home.hero.title': 'Festas do Pijama Mágicas',
    'home.hero.subtitle': 'Crie memórias inesquecíveis com nossas festas temáticas em tendas.\nPerfeitas para idades de 6 a 15 anos',
    'home.hero.cta': 'Reserve Sua Festa Hoje',
    'home.services.title': 'Por Que Escolher a Cabanna\'s Party?',
    'home.service1.title': 'Serviço Completo de Montagem',
    'home.service1.desc': 'Toda decoração montada e retirada no dia seguinte no horário desejado! Estamos comprometidos em proporcionar a melhor experiência para sua festa!',
    'home.service2.title': 'Decorações Personalizadas',
    'home.service2.desc': 'O tema da decoração pode ser selecionado pelo cliente, e inclui: Suporte personalizado para bolo e doces, Placas decorativas, Nuvem de balões, Fogueira artificial e muito mais!',
    'home.service3.title': 'Extras Incríveis',
    'home.service3.desc': 'Material extra está incluído: Sacolinhas de lembranças, Microfones de karaokê, jogos e muito mais para tornar sua festa inesquecível!',
    'home.feature1.title': 'Diversão Garantida!',
    'home.feature1.desc': 'Nossas festas do pijama temáticas são perfeitas para festas de pijama e criam experiências mágicas para toda a família.',
    'home.feature2.title': 'Entrega Gratuita',
    'home.feature2.desc': 'Fornecemos entrega e montagem gratuitas de nossas tendas temáticas, tornando o planejamento da sua festa livre de estresse.',
    'home.feature3.title': 'Baseados em Orlando',
    'home.feature3.desc': 'Estamos baseados em Orlando e comprometidos em proporcionar a melhor experiência de festa para crianças em toda a Flórida Central.',
    'home.testimonials.title': 'O Que Nossos Clientes Famosos Dizem',
    'home.testimonial1.text': 'A Cabanna\'s Party tornou o aniversário da minha filha absolutamente mágico! A atenção aos detalhes foi incrível.',
    'home.testimonial1.author': 'Deborah Secco',
    'home.testimonial1.role': 'Atriz & Apresentadora',
    'home.testimonial2.text': 'A melhor experiência de festa de todas! Meus filhos ainda falam sobre sua incrível festa do pijama.',
    'home.testimonial2.author': 'Fernanda Pontes',
    'home.testimonial2.role': 'Atriz',
    'home.testimonial3.text': 'Profissional, criativo e muito divertido! Recomendo altamente para qualquer celebração especial.',
    'home.testimonial3.author': 'Deive Leonardo',
    'home.testimonial3.role': 'Ator & Cantor',
    'home.testimonial4.text': 'Montagem de festa perfeita! As crianças se divertiram muito. Obrigada Cabanna\'s!',
    'home.testimonial4.author': 'Lori Improta',
    'home.testimonial4.role': 'Influenciadora Fitness',
    'home.testimonial5.text': 'Serviço incrível e decorações lindas. Tornou nossa celebração familiar inesquecível!',
    'home.testimonial5.author': 'Nivea Stelmann',
    'home.testimonial5.role': 'Atriz & Modelo',
    'home.themes.title': 'Temas Populares de Festa',
    'home.themes.description': 'Descubra nossos temas de festa infantil mais populares em Orlando. De festas de princesa a aventuras de super-heróis, criamos experiências mágicas de festa do pijama com tendas temáticas, decorações e serviço completo de montagem.',
    'home.theme.princess': 'Festa de Princesa',
    'home.theme.unicorn': 'Magia de Unicórnio',
    'home.theme.pokemon': 'Aventura Pokemon',
    'home.theme.superhero': 'Esquadrão de Super-Heróis',
    'home.cta.title': 'Festa do Pijama Significa Muita Diversão!',
    'home.cta.subtitle': 'Reserve agora sem compromissos e deixe-nos criar a experiência de festa perfeita para seu filho.',
    'home.cta.button': 'Ligue para Nosso Diretor de Festas',
    'home.themes.viewall': 'Ver Todos os Temas',

    // Gallery Page
    'gallery.title': 'Galeria de Festas',
    'gallery.subtitle': 'Navegue por nossas incríveis montagens e temas de festa.\nCada festa é projetada exclusivamente para criar memórias mágicas!',
    'gallery.instagram.title': '🌟 Fotos Mais Recentes das Festas',
    'gallery.instagram.desc': 'Siga-nos no Instagram para ver todas as nossas montagens mais recentes e se inspirar para o dia especial do seu filho!',
    'gallery.instagram.button': 'Siga-nos no Instagram',
    'gallery.themes.title': 'Temas de Festa',
    'gallery.tent.types.title': 'Escolha Sua Montagem Perfeita',
    'gallery.tent.types.subtitle': 'Oferecemos três opções incríveis de montagem para se adequar perfeitamente ao seu espaço e estilo de festa',
    'gallery.tent.small.title': 'TeePee',
    'gallery.tent.small.desc': 'Montagem interna aconchegante e íntima, perfeita para espaços menores. Apresenta áreas individuais para dormir com decorações mágicas e luzes de fada para uma experiência encantadora de festa do pijama.',
    'gallery.tent.canopy.title': 'Canopy Interno',
    'gallery.tent.canopy.desc': 'Montagem de festa interna ao ar livre sem paredes de tenda. Perfeita para espaços internos maiores, oferecendo flexibilidade e espaço para se mover mantendo a atmosfera mágica da festa do pijama.',
    'gallery.tent.big.title': 'Cabana Grande',
    'gallery.tent.big.desc': 'Nossa experiência premium de glamping ao ar livre! Perfeita para quintais, pátios ou espaços externos. Montagem luxuosa espaçosa com materiais resistentes ao clima e comodidades extras para a aventura definitiva de festa do pijama ao ar livre.',
    'gallery.process.title': 'Nosso Processo Mágico de Montagem',
    'gallery.process.subtitle': 'Da consulta à limpeza, cuidamos de tudo para sua festa perfeita',
    'gallery.process.step1.title': 'Consulta',
    'gallery.process.step1.desc': 'Discutimos suas preferências de tema, detalhes da festa e pedidos especiais',
    'gallery.process.step2.title': 'Montagem Profissional',
    'gallery.process.step2.desc': 'Nossa equipe especializada chega e cria o ambiente perfeito e mágico da festa',
    'gallery.process.step3.title': 'Hora da Festa!',
    'gallery.process.step3.desc': 'As crianças aproveitam sua experiência inesquecível e mágica de festa do pijama',
    'gallery.process.step4.title': 'Limpeza Fácil',
    'gallery.process.step4.desc': 'Voltamos no dia seguinte para limpar tudo - sem estresse para os pais!',
    'gallery.cta.title': 'Pronto para Criar Memórias Mágicas?',
    'gallery.cta.subtitle': 'Entre em contato conosco hoje para reservar a festa dos sonhos do seu filho!',
    'gallery.cta.button': 'Ligue para Nosso Diretor de Festas',

    // Contact Page
    'contact.title': 'Contato',
    'contact.subtitle': 'Pronto para planejar a festa perfeita?\nEntre em contato com nossos especialistas em festas!',
    'contact.phone.title': 'Ligue para Nosso Diretor de Festas',
    'contact.phone.hours': 'Disponível 7 dias por semana\n9h às 20h',
    'contact.email.title': 'Envie um Email',
    'contact.email.response': 'Respondemos em até 24 horas',
    'contact.location.title': 'Área de Atendimento',
    'contact.location.area': 'Orlando e Flórida Central',
    'contact.location.delivery': 'Entrega gratuita em um raio de 40 km',
    'contact.booking.title': 'Aviso de Reserva',
    'contact.booking.notice': 'Preferível 2 semanas de antecedência',
    'contact.booking.rush': 'Pedidos urgentes disponíveis',
    'contact.form.title': 'Solicitar Orçamento',
    'contact.faq.title': 'Perguntas Frequentes',
    'contact.faq.q1': 'Com que antecedência devo reservar?',
    'contact.faq.a1': 'Recomendamos reservar com pelo menos 2 semanas de antecedência, especialmente para fins de semana. No entanto, podemos acomodar pedidos urgentes quando possível.',
    'contact.faq.q2': 'O que está incluído no aluguel?',
    'contact.faq.a2': 'Cada tenda inclui roupa de cama, travesseiros, decorações temáticas, luzes de fada e uma placa de nome personalizada. Também fornecemos serviço de montagem e retirada.',
    'contact.faq.q3': 'Vocês fornecem comida ou entretenimento?',
    'contact.faq.a3': 'Focamos na montagem da festa do pijama e decorações. Podemos recomendar fornecedores locais confiáveis para catering, entretenimento e suprimentos para festas.',
    'contact.faq.q4': 'E se chover?',
    'contact.faq.a4': 'Nossas tendas são projetadas para uso interno. Trabalharemos com você para garantir que seu espaço de festa esteja pronto, faça chuva ou faça sol!',
    'contact.faq.q5': 'Como faço para enviar fotos para sua galeria?',
    'contact.faq.a5': 'Siga-nos no Instagram! Quando você nos marcar em suas fotos da festa ou nos enviar, destacaremos em nosso feed do Instagram e galeria do site.',
    'contact.faq.q6': 'Vocês viajam para fora de Orlando?',
    'contact.faq.a6': 'Sim! Fornecemos entrega gratuita em um raio de 40 km de Orlando. Para locais além disso, uma pequena taxa de viagem pode ser aplicada.',
    'contact.whatsapp.title': 'WhatsApp',
    'contact.whatsapp.subtitle': 'Resposta rápida garantida',
    'contact.whatsapp.message': 'Oi! Estou interessado em reservar uma festa do pijama mágica. Podem me ajudar com mais informações?',
    'contact.service.title': 'Informações do Serviço',
    'contact.service.subtitle': 'Tudo que você precisa saber sobre nossa área de atendimento e requisitos de reserva',

    // Footer
    'footer.description': 'Criando experiências mágicas de festa do pijama para crianças em toda a Flórida Central.',
    'footer.quicklinks': 'Links Rápidos',
    'footer.services': 'Nossos Serviços',
    'footer.contact': 'Informações de Contato',
    'footer.location': 'Orlando, FL e Áreas Circundantes',
    'footer.copyright': '© 2026 Cabanna\'s Party Rentals. Todos os direitos reservados.',
    'footer.privacy': 'Política de Privacidade',
    'footer.terms': 'Termos de Serviço',

    // Gallery Themes Data
    'gallery.themes.search.placeholder': 'Buscar temas de festa...',
    'gallery.themes.custom.note': 'Não encontrou seu tema? Entre em contato com nosso gerente de festas para criar um tema personalizado só para você!',
    'gallery.themes.no.results': 'Nenhum tema encontrado para "{query}". Entre em contato com nosso gerente de festas para temas personalizados!',
    'gallery.themes.princess.title': 'Festa de Princesa',
    'gallery.themes.princess.desc': 'Festas de princesa reais com decorações elegantes, tiaras e temas de castelo mágico para celebrações inesquecíveis.',
    'gallery.themes.unicorn.title': 'Magia de Unicórnio',
    'gallery.themes.unicorn.desc': 'Festas de unicórnio coloridas com decorações brilhantes e acessórios mágicos para sonhos encantados.',
    'gallery.themes.pokemon.title': 'Aventura Pokemon',
    'gallery.themes.pokemon.desc': 'Festas de aventura Pokemon cheias de ação com decorações de personagens e elementos emocionantes.',
    'gallery.themes.superhero.title': 'Esquadrão de Super-Heróis',
    'gallery.themes.superhero.desc': 'Festas temáticas de super-heróis com capas, máscaras e decorações cheias de ação para aventureiros corajosos.',
    'gallery.themes.space.title': 'Aventura Espacial',
    'gallery.themes.space.desc': 'Temas espaciais galácticos com estrelas, planetas, astronautas e decorações cósmicas para diversão fora deste mundo.',
    'gallery.themes.mermaid.title': 'Sonhos de Sereia',
    'gallery.themes.mermaid.desc': 'Festas de sereia no fundo do mar com azuis oceânicos, conchas e decorações mágicas subaquáticas.',
    'gallery.themes.dinosaur.title': 'Descoberta de Dinossauros',
    'gallery.themes.dinosaur.desc': 'Aventuras pré-históricas de dinossauros com temas de selva e elementos arqueológicos emocionantes.',
    'gallery.themes.fairy.title': 'Jardim das Fadas',
    'gallery.themes.fairy.desc': 'Festas encantadas de jardim das fadas com flores, borboletas e criaturas mágicas da floresta.',
    'gallery.themes.pirate.title': 'Aventura Pirata',
    'gallery.themes.pirate.desc': 'Ahoy marujo! Festas temáticas de piratas com caças ao tesouro, navios e aventuras de espadachins.',
    'gallery.themes.minecraft.title': 'Mundo Minecraft',
    'gallery.themes.minecraft.desc': 'Aventuras Minecraft em blocos com decorações pixeladas e temas de construção criativa.',
    'gallery.themes.frozen.title': 'Inverno Frozen',
    'gallery.themes.frozen.desc': 'Let it go com festas temáticas Frozen com azuis gelados, flocos de neve e magia do inverno.',
    'gallery.themes.safari.title': 'Aventura Safari',
    'gallery.themes.safari.desc': 'Expedições safari selvagens com animais da selva, equipamentos de aventura e temas de exploração.',
    'gallery.themes.rainbow.title': 'Sonhos Arco-Íris',
    'gallery.themes.rainbow.desc': 'Festas coloridas de arco-íris com decorações brilhantes, nuvens e temas de cores mágicas.',
    'gallery.themes.sports.title': 'Campeões Esportivos',
    'gallery.themes.sports.desc': 'Temas esportivos atléticos com cores de equipe, equipamentos e decorações de celebração de campeonato.',
    'gallery.themes.art.title': 'Estúdio de Arte',
    'gallery.themes.art.desc': 'Festas de arte criativas com materiais de pintura, decorações coloridas e inspiração artística.',
    'gallery.themes.music.title': 'Festival de Música',
    'gallery.themes.music.desc': 'Celebrações musicais com instrumentos, notas e decorações estilo concerto para pequenos rockstars.',
    'gallery.themes.carnival.title': 'Diversão de Carnaval',
    'gallery.themes.carnival.desc': 'Temas de carnaval circense com cores brilhantes, jogos e decorações festivas de carnaval.',
    'gallery.themes.beach.title': 'Festa na Praia',
    'gallery.themes.beach.desc': 'Festas tropicais na praia com areia, surf, palmeiras e vibrações ensolaradas de verão.',
    'gallery.themes.garden.title': 'Festa no Jardim',
    'gallery.themes.garden.desc': 'Belos temas de jardim com flores, borboletas e decorações naturais ao ar livre.',
    'gallery.themes.movie.title': 'Noite de Cinema',
    'gallery.themes.movie.desc': 'Temas de cinema de Hollywood com tapetes vermelhos, pipoca e decorações estilo cinema.'
  },
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.gallery': 'Galería',
    'nav.contact': 'Contacto',

    // Home Page
    'home.hero.title': 'Fiestas de Pijamas Mágicas',
    'home.hero.subtitle': 'Crea recuerdos inolvidables con fiestas temáticas en carpas.\nPerfectas para edades de 6 a 15 años',
    'home.hero.cta': 'Reserva Tu Fiesta Hoy',
    'home.services.title': '¿Por Qué Elegir Cabanna\'s Party?',
    'home.service1.title': 'Servicio Completo de Montaje',
    'home.service1.desc': '¡Toda la decoración montada y recogida al día siguiente a la hora deseada! ¡Estamos comprometidos a brindar la mejor experiencia para tu fiesta!',
    'home.service2.title': 'Decoraciones Personalizadas',
    'home.service2.desc': 'El tema de decoración puede ser seleccionado por el cliente, e incluye: Soporte personalizado para pastel y dulces, Letreros decorativos, Nube de globos, Fogata artificial ¡y mucho más!',
    'home.service3.title': 'Extras Increíbles',
    'home.service3.desc': 'Material extra está incluido: Bolsitas de recuerdos, Micrófonos de karaoke, juegos, ¡y mucho más para hacer tu fiesta inolvidable!',
    'home.feature1.title': '¡Diversión Garantizada!',
    'home.feature1.desc': 'Nuestras fiestas de pijamas temáticas son perfectas para fiestas de pijamas y crean experiencias mágicas para toda la familia.',
    'home.feature2.title': 'Entrega Gratuita',
    'home.feature2.desc': 'Proporcionamos entrega y montaje gratuitos de nuestras carpas temáticas, haciendo que la planificación de tu fiesta sea libre de estrés.',
    'home.feature3.title': 'Con Base en Orlando',
    'home.feature3.desc': 'Tenemos nuestra base en Orlando y estamos comprometidos a brindar la mejor experiencia de fiesta para niños en toda Florida Central.',
    'home.testimonials.title': 'Lo Que Dicen Nuestros Clientes Famosos',
    'home.testimonial1.text': '¡Cabanna\'s Party hizo el cumpleaños de mi hija absolutamente mágico! La atención al detalle fue increíble.',
    'home.testimonial1.author': 'Deborah Secco',
    'home.testimonial1.role': 'Actriz & Presentadora',
    'home.testimonial2.text': '¡La mejor experiencia de fiesta jamás! Mis hijos aún hablan de su increíble fiesta de pijamas.',
    'home.testimonial2.author': 'Fernanda Pontes',
    'home.testimonial2.role': 'Actriz',
    'home.testimonial3.text': '¡Profesional, creativo y muy divertido! Altamente recomendado para cualquier celebración especial.',
    'home.testimonial3.author': 'Deive Leonardo',
    'home.testimonial3.role': 'Actor & Cantante',
    'home.testimonial4.text': '¡Montaje de fiesta perfecto! Los niños se divirtieron muchísimo. ¡Gracias Cabanna\'s!',
    'home.testimonial4.author': 'Lori Improta',
    'home.testimonial4.role': 'Influencer Fitness',
    'home.testimonial5.text': '¡Servicio increíble y decoraciones hermosas. Hizo nuestra celebración familiar inolvidable!',
    'home.testimonial5.author': 'Nivea Stelmann',
    'home.testimonial5.role': 'Actriz & Modelo',
    'home.themes.title': 'Temas Populares de Fiesta',
    'home.themes.description': 'Descubre nuestros temas de fiesta infantil más populares en Orlando. Desde fiestas de princesas hasta aventuras de superhéroes, creamos experiencias mágicas de fiestas de pijamas con carpas temáticas, decoraciones y servicio completo de montaje.',
    'home.theme.princess': 'Fiesta de Princesas',
    'home.theme.unicorn': 'Magia de Unicornio',
    'home.theme.pokemon': 'Aventura Pokemon',
    'home.theme.superhero': 'Escuadrón de Superhéroes',
    'home.cta.title': '¡Fiesta de Pijamas Significa Mucha Diversión!',
    'home.cta.subtitle': 'Reserva ahora sin compromisos y déjanos crear la experiencia de fiesta perfecta para tu hijo.',
    'home.cta.button': 'Llama a Nuestro Director de Fiestas',
    'home.themes.viewall': 'Ver Todos los Temas',

    // Gallery Page
    'gallery.title': 'Galería de Fiestas',
    'gallery.subtitle': 'Explora nuestras increíbles configuraciones y temas de fiesta.\n¡Cada fiesta está diseñada únicamente para crear recuerdos mágicos!',
    'gallery.instagram.title': '🌟 Fotos Más Recientes de Fiestas',
    'gallery.instagram.desc': '¡Síguenos en Instagram para ver todos nuestros montajes más recientes y obtener inspiración para el día especial de tu hijo!',
    'gallery.instagram.button': 'Síguenos en Instagram',
    'gallery.themes.title': 'Temas de Fiesta',
    'gallery.tent.types.title': 'Elige Tu Montaje Perfecto',
    'gallery.tent.types.subtitle': 'Ofrecemos tres opciones increíbles de montaje para adaptarse perfectamente a tu espacio y estilo de fiesta',
    'gallery.tent.small.title': 'TeePee',
    'gallery.tent.small.desc': 'Montaje interior acogedor e íntimo, perfecto para espacios más pequeños. Presenta áreas individuales para dormir con decoraciones mágicas y luces de hadas para una experiencia encantadora de fiesta de pijamas.',
    'gallery.tent.canopy.title': 'Canopy Interior',
    'gallery.tent.canopy.desc': 'Montaje de fiesta interior al aire libre sin paredes de carpa. Perfecto para espacios interiores más grandes, ofreciendo flexibilidad y espacio para moverse manteniendo la atmósfera mágica de la fiesta de pijamas.',
    'gallery.tent.big.title': 'Cabana Grande',
    'gallery.tent.big.desc': '¡Nuestra experiencia premium de glamping al aire libre! Perfecta para patios traseros, terrazas o espacios exteriores. Montaje lujoso espacioso con materiales resistentes al clima y comodidades extra para la aventura definitiva de fiesta de pijamas al aire libre.',
    'gallery.process.title': 'Nuestro Proceso Mágico de Montaje',
    'gallery.process.subtitle': 'Desde la consulta hasta la limpieza, nos encargamos de todo para tu fiesta perfecta',
    'gallery.process.step1.title': 'Consulta',
    'gallery.process.step1.desc': 'Discutimos tus preferencias de tema, detalles de la fiesta y solicitudes especiales',
    'gallery.process.step2.title': 'Montaje Profesional',
    'gallery.process.step2.desc': 'Nuestro equipo experto llega y crea el ambiente perfecto y mágico de la fiesta',
    'gallery.process.step3.title': '¡Hora de la Fiesta!',
    'gallery.process.step3.desc': 'Los niños disfrutan de su experiencia inolvidable y mágica de fiesta de pijamas',
    'gallery.process.step4.title': 'Limpieza Fácil',
    'gallery.process.step4.desc': '¡Regresamos al día siguiente para limpiar todo - sin estrés para los padres!',
    'gallery.cta.title': '¿Listo para Crear Recuerdos Mágicos?',
    'gallery.cta.subtitle': '¡Contáctanos hoy para reservar la fiesta de ensueño de tu hijo!',
    'gallery.cta.button': 'Llama a Nuestro Director de Fiestas',

    // Contact Page
    'contact.title': 'Contacto',
    'contact.subtitle': '¿Listo para planear la fiesta perfecta?\n¡Ponte en contacto con nuestros expertos en planificación de fiestas!',
    'contact.phone.title': 'Llama a Nuestro Director de Fiestas',
    'contact.phone.hours': 'Disponible 7 días a la semana\n9 AM - 8 PM',
    'contact.email.title': 'Envíanos un Email',
    'contact.email.response': 'Respondemos dentro de 24 horas',
    'contact.location.title': 'Área de Servicio',
    'contact.location.area': 'Orlando y Florida Central',
    'contact.location.delivery': 'Entrega gratuita dentro de 25 millas',
    'contact.booking.title': 'Aviso de Reserva',
    'contact.booking.notice': 'Se prefiere 2 semanas de anticipación',
    'contact.booking.rush': 'Pedidos urgentes disponibles',
    'contact.form.title': 'Solicitar Cotización',
    'contact.faq.title': 'Preguntas Frecuentes',
    'contact.faq.q1': '¿Con cuánta anticipación debo reservar?',
    'contact.faq.a1': 'Recomendamos reservar con al menos 2 semanas de anticipación, especialmente para fines de semana. Sin embargo, podemos acomodar pedidos urgentes cuando sea posible.',
    'contact.faq.q2': '¿Qué está incluido en el alquiler?',
    'contact.faq.a2': 'Cada carpa incluye ropa de cama, almohadas, decoraciones temáticas, luces de hadas y un letrero de nombre personalizado. También proporcionamos servicio de montaje y recogida.',
    'contact.faq.q3': '¿Proporcionan comida o entretenimiento?',
    'contact.faq.a3': 'Nos enfocamos en el montaje de la fiesta de pijamas y decoraciones. Podemos recomendar proveedores locales confiables para catering, entretenimiento y suministros para fiestas.',
    'contact.faq.q4': '¿Qué pasa si llueve?',
    'contact.faq.a4': 'Nuestras carpas están diseñadas para uso interior. ¡Trabajaremos contigo para asegurar que tu espacio de fiesta esté listo, llueva o haga sol!',
    'contact.faq.q5': '¿Cómo subo fotos a su galería?',
    'contact.faq.a5': '¡Síguenos en Instagram! Cuando nos etiquetes en las fotos de tu fiesta o nos las envíes, las destacaremos en nuestro feed de Instagram y galería del sitio web.',
    'contact.faq.q6': '¿Viajan fuera de Orlando?',
    'contact.faq.a6': '¡Sí! Proporcionamos entrega gratuita dentro de 25 millas de Orlando. Para ubicaciones más allá de eso, puede aplicarse una pequeña tarifa de viaje.',
    'contact.whatsapp.title': 'WhatsApp',
    'contact.whatsapp.subtitle': 'Respuesta rápida garantizada',
    'contact.whatsapp.message': '¡Hola! Estoy interesado en reservar una fiesta de pijamas mágica. ¿Pueden ayudarme con más información?',
    'contact.service.title': 'Información del Servicio',
    'contact.service.subtitle': 'Todo lo que necesitas saber sobre nuestra área de servicio y requisitos de reserva',

    // Footer
    'footer.description': 'Creando experiencias mágicas de fiestas de pijamas para niños en toda Florida Central.',
    'footer.quicklinks': 'Enlaces Rápidos',
    'footer.services': 'Nuestros Servicios',
    'footer.contact': 'Información de Contacto',
    'footer.location': 'Orlando, FL y Áreas Circundantes',
    'footer.copyright': '© 2026 Cabanna\'s Party Rentals. Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',

    // Gallery Themes Data
    'gallery.themes.search.placeholder': 'Buscar temas de fiesta...',
    'gallery.themes.custom.note': '¿No encuentras tu tema? ¡Contacta a nuestro gerente de fiestas para crear un tema personalizado solo para ti!',
    'gallery.themes.no.results': 'No se encontraron temas para "{query}". ¡Contacta a nuestro gerente de fiestas para temas personalizados!',
    'gallery.themes.princess.title': 'Fiesta de Princesas',
    'gallery.themes.princess.desc': 'Fiestas de princesas reales con decoraciones elegantes, tiaras y temas de castillo mágico para celebraciones inolvidables.',
    'gallery.themes.unicorn.title': 'Magia de Unicornio',
    'gallery.themes.unicorn.desc': 'Fiestas de unicornio coloridas con decoraciones brillantes y accesorios mágicos para sueños encantados.',
    'gallery.themes.pokemon.title': 'Aventura Pokemon',
    'gallery.themes.pokemon.desc': 'Fiestas de aventura Pokemon llenas de acción con decoraciones de personajes y elementos emocionantes.',
    'gallery.themes.superhero.title': 'Escuadrón de Superhéroes',
    'gallery.themes.superhero.desc': 'Fiestas temáticas de superhéroes con capas, máscaras y decoraciones llenas de acción para aventureros valientes.',
    'gallery.themes.space.title': 'Aventura Espacial',
    'gallery.themes.space.desc': 'Temas espaciales galácticos con estrellas, planetas, astronautas y decoraciones cósmicas para diversión fuera de este mundo.',
    'gallery.themes.mermaid.title': 'Sueños de Sirena',
    'gallery.themes.mermaid.desc': 'Fiestas de sirenas bajo el mar con azules oceánicos, conchas y decoraciones mágicas submarinas.',
    'gallery.themes.dinosaur.title': 'Descubrimiento de Dinosaurios',
    'gallery.themes.dinosaur.desc': 'Aventuras prehistóricas de dinosaurios con temas de selva y elementos arqueológicos emocionantes.',
    'gallery.themes.fairy.title': 'Jardín de Hadas',
    'gallery.themes.fairy.desc': 'Fiestas encantadas de jardín de hadas con flores, mariposas y criaturas mágicas del bosque.',
    'gallery.themes.pirate.title': 'Aventura Pirata',
    'gallery.themes.pirate.desc': '¡Ahoy marinero! Fiestas temáticas de piratas con búsquedas del tesoro, barcos y aventuras de espadachines.',
    'gallery.themes.minecraft.title': 'Mundo Minecraft',
    'gallery.themes.minecraft.desc': 'Aventuras Minecraft en bloques con decoraciones pixeladas y temas de construcción creativa.',
    'gallery.themes.frozen.title': 'Invierno Frozen',
    'gallery.themes.frozen.desc': 'Let it go con fiestas temáticas Frozen con azules helados, copos de nieve y magia invernal.',
    'gallery.themes.safari.title': 'Aventura Safari',
    'gallery.themes.safari.desc': 'Expediciones safari salvajes con animales de la selva, equipo de aventura y temas de exploración.',
    'gallery.themes.rainbow.title': 'Sueños Arcoíris',
    'gallery.themes.rainbow.desc': 'Fiestas coloridas de arcoíris con decoraciones brillantes, nubes y temas de colores mágicos.',
    'gallery.themes.sports.title': 'Campeones Deportivos',
    'gallery.themes.sports.desc': 'Temas deportivos atléticos con colores de equipo, equipamiento y decoraciones de celebración de campeonato.',
    'gallery.themes.art.title': 'Estudio de Arte',
    'gallery.themes.art.desc': 'Fiestas de arte creativas con materiales de pintura, decoraciones coloridas e inspiración artística.',
    'gallery.themes.music.title': 'Festival de Música',
    'gallery.themes.music.desc': 'Celebraciones musicales con instrumentos, notas y decoraciones estilo concierto para pequeños rockstars.',
    'gallery.themes.carnival.title': 'Diversión de Carnaval',
    'gallery.themes.carnival.desc': 'Temas de carnaval circense con colores brillantes, juegos y decoraciones festivas de carnaval.',
    'gallery.themes.beach.title': 'Fiesta en la Playa',
    'gallery.themes.beach.desc': 'Fiestas tropicales en la playa con arena, surf, palmeras y vibraciones soleadas de verano.',
    'gallery.themes.garden.title': 'Fiesta en el Jardín',
    'gallery.themes.garden.desc': 'Hermosos temas de jardín con flores, mariposas y decoraciones naturales al aire libre.',
    'gallery.themes.movie.title': 'Noche de Película',
    'gallery.themes.movie.desc': 'Temas de película de Hollywood con alfombras rojas, palomitas y decoraciones estilo cinema.'
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'pt' || savedLanguage === 'es')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  // Save language preference to localStorage
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('preferred-language', lang);
  };

  // Translation function
  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
